import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

import { env } from '../env'
import store from '../reducers'
import { setIsAuthenticated } from '../reducers/authReducer'
import { JWT_TOKEN_KEY_NAME } from '../utils/constants'

let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []
const pendingRequests = new Map()
const TIMEOUT = 500

function onRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token))
  refreshSubscribers = []
}

function generateRequestKey(config: InternalAxiosRequestConfig<unknown>) {
  const { method, url, data, params } = config
  return `${method}-${url}-${JSON.stringify(data)}-${JSON.stringify(params)}`
}

function createApiInstance() {
  const api = axios.create({
    baseURL: env.VITE_APP_API,
    withCredentials: true,
  })

  api.interceptors.request.use(
    async (config) => {
      let token = localStorage.getItem(JWT_TOKEN_KEY_NAME)
      if (token) {
        token = token.replace(/["']/g, '')
        config.headers.Authorization = `Bearer ${token}`
      }

      const requestKey = generateRequestKey(config)
      if (pendingRequests.has(requestKey)) {
        return Promise.reject({
          code: 'DUPLICATE_REQUEST',
          message: `Requisição bloqueada devido a duplicação: ${requestKey}`,
        })
      }
      pendingRequests.set(requestKey, true)
      setTimeout(() => pendingRequests.delete(requestKey), TIMEOUT)

      return config
    },
    (error) => Promise.reject(error),
  )

  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (axios.isCancel(error)) {
        return Promise.resolve()
      }

      if (!axios.isAxiosError(error)) {
        return Promise.reject(error)
      }

      const err = error as AxiosError
      const dispatch = store.dispatch
      const originalRequest = err.config as InternalAxiosRequestConfig<unknown> & { _retry: boolean }

      if (err.response?.status === 401 && originalRequest && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve) => {
            refreshSubscribers.push((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              resolve(api.request(originalRequest))
            })
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const response = await api.patch('/token/refresh')

          const { token } = response.data
          localStorage.setItem(JWT_TOKEN_KEY_NAME, token)
          onRefreshed(token)

          // Atualiza o header globalmente
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
          isRefreshing = false

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`
          }
          return api.request(originalRequest)
        } catch (err) {
          isRefreshing = false
          refreshSubscribers = []
          localStorage.removeItem(JWT_TOKEN_KEY_NAME)
          store.dispatch(setIsAuthenticated(false))
          return Promise.reject(err)
        }
      }

      if (err.response?.status !== 401) {
        return Promise.reject(error)
      }

      localStorage.removeItem(JWT_TOKEN_KEY_NAME)
      dispatch(setIsAuthenticated(false))
      return Promise.reject(error)
    },
  )

  return api
}

export const api = createApiInstance()
